import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

export interface UserDefaultBranch { legalEntityId: string; operatingUnitId: string; branchCode: string; branchName: string; active: boolean }
export interface TechnicianBranch { legalEntityId: string; operatingUnitId: string; branchCode: string; branchName: string; active: boolean }
export interface UserAccessGrant { legalEntityId: string; role: string; allOperatingUnits: boolean; operatingUnitIds: string[] }
export interface CurrentUserAccess { email: string; roles: string[]; defaultBranches: UserDefaultBranch[]; technicianBranches?: TechnicianBranch[]; accessGrants?: UserAccessGrant[] }
export interface RoleDefinition { code: string; name: string; purpose: string }

export const FALLBACK_ROLE_DEFINITIONS: RoleDefinition[] = [
  { code: "ERP_ADMIN", name: "ERP administrator", purpose: "Identity, company configuration, and all application functions" },
  { code: "CONTROLLER", name: "Controller", purpose: "Financial control, approvals, close, and reconciliation" },
  { code: "ACCOUNTANT", name: "Accountant", purpose: "General ledger and financial operations" },
  { code: "AP", name: "Accounts payable", purpose: "Vendor invoices and payments" },
  { code: "AP_LEAD", name: "AP lead", purpose: "Voucher review and vendor extraction-rule approval" },
  { code: "AR", name: "Accounts receivable", purpose: "Customer invoices and receipts" },
  { code: "COLLECTIONS", name: "Collector", purpose: "Worklists and full customer accounts" },
  { code: "COLLECTIONS_READ", name: "Collections observer", purpose: "Read full authorized customer accounts" },
  { code: "COLLECTIONS_LEGAL", name: "Collections legal access", purpose: "Restricted legal files and attorney notes" },
  { code: "COLLECTIONS_RESEARCH", name: "Collections research", purpose: "Optional reviewed public-source research" },
  { code: "CFO", name: "CFO", purpose: "Collections financial controls and approvals" },
  { code: "OWNER", name: "Ownership", purpose: "Collections financial controls and approvals" },
  { code: "CREDIT_RELEASE", name: "Credit release authorization", purpose: "Approve and revoke order-specific Finance authorizations" },
  { code: "CREDIT_MANAGER", name: "Credit manager", purpose: "Customer credit decisions" },
  { code: "PURCHASING", name: "Purchasing", purpose: "Purchase orders and receipts" },
  { code: "PURCHASING_MANAGER", name: "Purchasing manager", purpose: "Purchase-order approval" },
  { code: "PARTS", name: "Parts", purpose: "Parts catalog, inventory, and work-order parts" },
  { code: "PARTS_MANAGER", name: "Parts manager", purpose: "Parts oversight" },
  { code: "EQUIPMENT_COST_VIEW", name: "Equipment cost access", purpose: "Explicit equipment cost and WIP access" },
  { code: "QUOTE_MARGIN", name: "Quote margin pricing", purpose: "View and enter quote margins; requires cost access" },
  { code: "EQUIPMENT_FINANCING_VIEW", name: "Equipment financing access", purpose: "View unit financing and principal" },
  { code: "EQUIPMENT", name: "Equipment", purpose: "Serialized equipment lifecycle" },
  { code: "SERVICE", name: "Service", purpose: "Service work-order operations" },
  { code: "SERVICE_ADVISOR", name: "Service advisor", purpose: "Customer-facing work-order operations" },
  { code: "SERVICE_MANAGER", name: "Service manager", purpose: "Service oversight and reconciliation" },
  { code: "TECHNICIAN", name: "Technician", purpose: "Assigned service work" },
  { code: "TELEMATICS", name: "Telematics integration", purpose: "Machine-hour ingestion" },
  { code: "WARRANTY", name: "Warranty", purpose: "Warranty claims" },
  { code: "RENTAL", name: "Rental", purpose: "Rental lifecycle" },
  { code: "RENTAL_PREP", name: "Rental prep", purpose: "Branch prep, inspections and exercising; no rental billing" },
  { code: "RENTAL_MANAGER", name: "Rental manager", purpose: "Rental oversight" },
  { code: "SALES", name: "Sales", purpose: "CRM, quotes, and orders" },
  { code: "OPERATIONS", name: "Operations", purpose: "Cross-functional operational oversight" },
  { code: "LOGISTICS_DISPATCH", name: "Logistics dispatcher", purpose: "Loads, routes, and drivers" },
  { code: "DRIVER", name: "Driver", purpose: "Assigned mobile routes" },
];

export const PM_CONTROL_ROLES = ["SERVICE", "SERVICE_MANAGER", "PARTS", "PARTS_MANAGER", "TELEMATICS", "OPERATIONS"];

const FINANCE = ["ERP_ADMIN", "CONTROLLER", "ACCOUNTANT"];
const routeRoles: Record<string, string[] | undefined> = {
  "/field-service": ["ERP_ADMIN", "OPERATIONS", "SERVICE_MANAGER"],
  "/": undefined,
  "/crm": undefined,
  "/pilot": undefined,
  "/finance": FINANCE,
  "/ap-payments": ["ERP_ADMIN", "CONTROLLER", "CFO", "AP", "AP_LEAD"],
  "/payables": [...FINANCE, "AP", "AP_LEAD"],
  "/collections": ["ERP_ADMIN", "COLLECTIONS", "COLLECTIONS_READ", "AR", "CREDIT_MANAGER", "CFO", "OWNER", "CONTROLLER", "ACCOUNTANT"],
  "/monthly-ar-statements": ["ERP_ADMIN", "AR", "CREDIT_MANAGER", "CFO", "OWNER", "CONTROLLER", "ACCOUNTANT"],
  "/cash-receipts": [...FINANCE, "AR"],
  "/receivables": [...FINANCE, "AR", "CREDIT_MANAGER"],
  "/rental-billing": ["ERP_ADMIN", "CONTROLLER", "AR", "RENTAL", "RENTAL_MANAGER", "OPERATIONS"],
  "/intercompany": FINANCE,
  "/financial-migration": FINANCE,
  "/fixed-assets": FINANCE,
  "/equipment-financing": FINANCE,
  "/integrity": FINANCE,
  "/reporting": FINANCE,
  "/parts": ["ERP_ADMIN", "PARTS", "PARTS_MANAGER", "PURCHASING", "SERVICE", "SERVICE_ADVISOR", "SERVICE_MANAGER", "OPERATIONS", "CONTROLLER", "ACCOUNTANT"],
  "/replenishment": ["ERP_ADMIN", "PARTS", "PARTS_MANAGER", "PURCHASING", "PURCHASING_MANAGER", "OPERATIONS"],
  "/inventory-transfers": ["ERP_ADMIN", "PARTS", "PARTS_MANAGER", "PURCHASING", "PURCHASING_MANAGER", "AP", "CONTROLLER", "OPERATIONS"],
  "/parts-fulfillment": ["ERP_ADMIN", "PARTS", "PARTS_MANAGER"],
  "/rental-parts": ["ERP_ADMIN", "PARTS", "PARTS_MANAGER", "RENTAL", "RENTAL_MANAGER", "OPERATIONS"],
  "/printing": ["ERP_ADMIN", "PARTS", "PARTS_MANAGER"],
  "/purchasing": ["ERP_ADMIN", "PURCHASING", "PURCHASING_MANAGER", "PARTS", "PARTS_MANAGER", "CONTROLLER", "OPERATIONS"],
  "/equipment": ["ERP_ADMIN", "EQUIPMENT", "SALES", "RENTAL", "RENTAL_MANAGER", "SERVICE", "SERVICE_ADVISOR", "SERVICE_MANAGER", "OPERATIONS"],
  "/service": ["ERP_ADMIN", "SERVICE", "SERVICE_ADVISOR", "SERVICE_MANAGER", "TECHNICIAN", "PARTS", "PARTS_MANAGER", "WARRANTY", "CONTROLLER", "OPERATIONS"],
  "/triage": ["ERP_ADMIN", "SERVICE_MANAGER", "TELEMATICS"],
  "/service-campaigns": ["ERP_ADMIN", "SERVICE_MANAGER", "SERVICE_ADVISOR", "SERVICE", "WARRANTY", "CONTROLLER", "EQUIPMENT", "RENTAL_MANAGER"],
  "/maintenance": ["ERP_ADMIN", "SERVICE", "SERVICE_ADVISOR", "SERVICE_MANAGER", "TECHNICIAN", "PARTS", "PARTS_MANAGER", "EQUIPMENT", "TELEMATICS", "OPERATIONS"],
  "/warranty": ["ERP_ADMIN", "WARRANTY", "SERVICE", "SERVICE_ADVISOR", "SERVICE_MANAGER", "CONTROLLER", "OPERATIONS"],
  "/rental": ["ERP_ADMIN", "RENTAL", "RENTAL_MANAGER", "OPERATIONS"],
  "/rental-prep": ["ERP_ADMIN", "RENTAL_PREP", "RENTAL", "RENTAL_MANAGER", "OPERATIONS", "EQUIPMENT", "SERVICE_MANAGER"],
  "/logistics": ["ERP_ADMIN", "LOGISTICS_DISPATCH", "RENTAL", "RENTAL_MANAGER", "SERVICE_MANAGER", "SALES", "CONTROLLER", "OPERATIONS"],
  "/sales": ["ERP_ADMIN", "SALES", "OPERATIONS"],
  "/credit-authorizations": ["ERP_ADMIN", "CREDIT_RELEASE", "CREDIT_MANAGER", "CONTROLLER"],
  "/counter-sales": ["ERP_ADMIN", "PARTS", "PARTS_MANAGER", "SALES", "OPERATIONS"],
  "/customer-parts-returns": ["ERP_ADMIN", "PARTS", "PARTS_MANAGER", "SALES", "RENTAL", "RENTAL_MANAGER", "CONTROLLER", "OPERATIONS"],
  "/customers": ["ERP_ADMIN", "CONTROLLER", "SALES", "AR", "CREDIT_MANAGER", "RENTAL", "RENTAL_MANAGER", "SERVICE", "SERVICE_ADVISOR", "SERVICE_MANAGER", "OPERATIONS"],
  "/vendors": ["ERP_ADMIN", "CONTROLLER", "PURCHASING", "PURCHASING_MANAGER", "AP", "PARTS", "PARTS_MANAGER", "OPERATIONS"],
  "/conversion": ["ERP_ADMIN", "CONTROLLER"],
  "/configuration": [...PM_CONTROL_ROLES, "ERP_ADMIN", "COLLECTIONS", "COLLECTIONS_READ", "AR", "CREDIT_MANAGER", "CFO", "OWNER", "CONTROLLER", "ACCOUNTANT"],
};

export function canAccessRoute(path: string, roles?: string[]): boolean {
  if (!roles) return true;
  const required = routeRoles[path];
  return !required || roles.includes("ERP_ADMIN") || required.some((role) => roles.includes(role));
}

export function hasAnyRole(roles: string[] | undefined, ...required: string[]): boolean {
  return Boolean(roles?.includes("ERP_ADMIN") || required.some((role) => roles?.includes(role)));
}

export function rolesForCompany(access: CurrentUserAccess | undefined, legalEntityId: string | undefined): string[] | undefined {
  if (!access) return undefined;
  if (access.roles.includes("ERP_ADMIN") || !access.accessGrants) return access.roles;
  if (!legalEntityId) return [];
  return [...new Set(access.accessGrants.filter((grant) => grant.legalEntityId === legalEntityId).map((grant) => grant.role))].sort();
}

export function userInitials(email?: string): string {
  if (!email) return "—";
  const local = email.split("@")[0] ?? email;
  const words = local.split(/[._-]+/).filter(Boolean);
  return (words.length > 1 ? words.slice(0, 2).map((word) => word[0]) : [local[0], local[1]])
    .filter(Boolean).join("").toUpperCase();
}

export function useCurrentAccess() {
  return useQuery({ queryKey: ["current-access"], queryFn: () => api<CurrentUserAccess>("/api/v1/access/me"), staleTime: 5 * 60_000 });
}
